declare global {
    interface Window {
        dataLayer: unknown[];
        gtag?: (...args: any[]) => void;
    }
}

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
let isInitialized = false;

const ensureDataLayer = () => {
    if (typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];
    if (!window.gtag) {
        window.gtag = (...args: any[]) => {
            window.dataLayer.push(args);
        };
    }
};

export const initAnalytics = () => {
    if (!GA_MEASUREMENT_ID) return false;
    if (isInitialized) return true;

    ensureDataLayer();

    const existingScript = document.getElementById("ga-gtag");
    if (!existingScript) {
        const script = document.createElement("script");
        script.id = "ga-gtag";
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
        document.head.appendChild(script);
    }

    window.gtag?.("js", new Date());
    // Disable auto page view so we can control it via router events
    window.gtag?.("config", GA_MEASUREMENT_ID, { send_page_view: false });

    isInitialized = true;
    return true;
};

export const trackPageView = (path: string) => {
    if (!GA_MEASUREMENT_ID) return;
    ensureDataLayer();
    window.gtag?.("event", "page_view", { page_path: path });
};

export const trackEvent = (
    action: string,
    params?: Record<string, string | number | boolean | undefined>
) => {
    if (!GA_MEASUREMENT_ID) return;
    ensureDataLayer();
    window.gtag?.("event", action, params);
};

// Set user ID for tracking active/new users
export const setUserId = (userId: string | number) => {
    if (!GA_MEASUREMENT_ID) return;
    ensureDataLayer();
    window.gtag?.("config", GA_MEASUREMENT_ID, {
        user_id: userId.toString(),
    });
};

// Set user properties (e.g., user type, subscription status, etc.)
export const setUserProperties = (properties: Record<string, any>) => {
    if (!GA_MEASUREMENT_ID) return;
    ensureDataLayer();
    window.gtag?.("set", "user_properties", properties);
};

// Track specific user events
export const trackUserEvent = {
    login: (userId: string | number, method = "phone") => {
        setUserId(userId);
        trackEvent("login", { method });
    },

    signup: (userId: string | number, method = "phone") => {
        setUserId(userId);
        trackEvent("sign_up", { method });
    },

    raffleEnter: (raffleId: string | number, raffleName: string, ticketsUsed: number) => {
        trackEvent("raffle_enter", {
            raffle_id: raffleId.toString(),
            raffle_name: raffleName,
            tickets_used: ticketsUsed,
        });
    },

    ticketEarned: (source: string, amount: number) => {
        trackEvent("ticket_earned", {
            source,
            amount,
        });
    },

    profileUpdate: (field: string) => {
        trackEvent("profile_update", { field });
    },

    questionAnswered: (questionId: string | number, correct: boolean) => {
        trackEvent("question_answered", {
            question_id: questionId.toString(),
            correct,
        });
    },

    referralSent: () => {
        trackEvent("referral_sent");
    },

    rewardClaimed: (rewardType: string, rewardValue: string | number) => {
        trackEvent("reward_claimed", {
            reward_type: rewardType,
            reward_value: rewardValue.toString(),
        });
    },
};


// Phase 1 funnel events
export const trackFunnel = {
    gamecenterOpen: () => {
        trackEvent("gamecenter_open");
    },

    phoneNumberEntered: () => {
        trackEvent("phone_number_entered");
    },

    otpSent: (success: boolean) => {
        trackEvent("otp_sent", { success });
    },

    otpVerified: (success: boolean, latencyMs?: number, failureReason?: string) => {
        trackEvent("otp_verified", {
            success,
            ...(latencyMs !== undefined && { latency_ms: latencyMs }),
            ...(failureReason && { failure_reason: failureReason }),
        });
    },

    otpAbandoned: (waitTimeMs: number) => {
        trackEvent("otp_abandoned", { wait_time_ms: waitTimeMs });
    },

    gameSelected: (gameType: string, gameId: string | number, gameName: string) => {
        trackEvent("game_selected", {
            game_type: gameType,
            game_id: gameId.toString(),
            game_name: gameName,
        });
    },

    rewardReveal: (rewardType: string, rewardValue: string, won: boolean) => {
        trackEvent("reward_reveal", {
            reward_type: rewardType,
            reward_value: rewardValue,
            won,
        });
    },

    copyCode: (codeType: string) => {
        trackEvent("copy_code", { code_type: codeType });
    },

    choiceMade: (choice: "daily_game" | "weekly_raffle" | "referral" | "profile" | "questions") => {
        trackEvent("choice_made", { choice });
    },

    questionStart: (milestoneId: number, questionIndex: number, totalQuestions: number) => {
        trackEvent("question_start", {
            milestone_id: milestoneId,
            question_index: questionIndex,
            total_questions: totalQuestions,
        });
    },

    questionComplete: (milestoneId: number, questionsAnswered: number) => {
        trackEvent("question_complete", {
            milestone_id: milestoneId,
            questions_answered: questionsAnswered,
        });
    },

    exitPoint: (currentPage: string) => {
        trackEvent("exit_point", { current_page: currentPage });
    },
};

// Track button/element clicks with custom context
export const trackClick = (
    elementName: string,
    context?: Record<string, string | number | boolean | undefined>
) => {
    trackEvent("click", {
        element_name: elementName,
        ...context,
    });
};
